import { IconExternalLink } from '@tabler/icons-react';
import { useParams } from 'react-router';
import { conversationsUrl } from '../ChatBotPage/conversationFormat';
import { salesAgentEventDetail, salesAgentEventLabel } from './salesAgentEvents';

const ProfileEventLink = ({
  event,
  pages,
  campaigns,
  emails,
  textMessages,
  viberMessages,
  pushNotifications,
  whatsappMessages,
}) => {
  const { accountId, domainId } = useParams();

  const eventElement = [];
  let i = 0;

  let page = pages.find((p) => p.token === event.pageToken || (p.pathname && event.url?.includes(p.pathname)));

  if (page) {
    const link = `#/accounts/${accountId}/domains/${domainId}/pages?page=${page.id}&type=recommenders`;
    eventElement.push(
      <div key={i++}>
        Page:{' '}
        <a href={link} target="_blank" rel="noreferrer noopener">
          {page.name} <IconExternalLink size={14} />
        </a>
      </div>
    );
  }
  if (event.url) {
    if (event.action === 'emailSend') {
      eventElement.push(
        <div key={i++}>
          Email content:{' '}
          <a href={event.url} target="_blank" rel="noreferrer noopener">
            View in browser <IconExternalLink size={14} />
          </a>
        </div>
      );
    } else {
      eventElement.push(
        <div key={i++}>
          URL:{' '}
          <a href={event.url} target="_blank" rel="noreferrer noopener">
            View in browser <IconExternalLink size={14} />
          </a>
        </div>
      );
    }
  }
  if (salesAgentEventLabel(event)) {
    const detail = salesAgentEventDetail(event);
    if (detail) eventElement.push(<div key={i++}>{detail}</div>);
    const chatSessionId = event.data?.chatSessionId;
    if (chatSessionId) {
      const link = `#${conversationsUrl(`/accounts/${accountId}/domains/${domainId}/chatBot`, chatSessionId)}`;
      eventElement.push(
        <div key={i++}>
          Conversation:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            Open <IconExternalLink size={14} />
          </a>
        </div>
      );
    }
  }
  if (event.action === 'pageEngagement') {
    // scrollPercent / activeTimeMs are stripped when zero (see cleanEmptyValues
    // on the API), so default missing values to 0.
    const scrollPercent = Number(event.scrollPercent) || 0;
    const activeSeconds = Math.round((Number(event.activeTimeMs) || 0) / 1000);
    const minutes = Math.floor(activeSeconds / 60);
    const seconds = activeSeconds % 60;
    const timeOnPage = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    eventElement.push(<div key={i++}>Scroll depth: {scrollPercent}%</div>);
    eventElement.push(<div key={i++}>Time on page: {timeOnPage}</div>);
  }

  if (event.action === 'autocompleteInteraction' && event.products?.length) {
    const resultCount = event.products.length;
    eventElement.push(
      <div key={i++}>
        Results: {resultCount} product{resultCount === 1 ? '' : 's'}
      </div>
    );
  }

  for (const product of event.products || []) {
    for (const page of pages) {
      const banner = page.bannerBlocks.find((x) => x.token === product.bannerBlockId);
      if (banner) {
        const link = `#/accounts/${accountId}/domains/${domainId}/pages/${banner.pageId}/bannerBlocks/${banner.id}/details`;
        eventElement.push(
          <div key={i++}>
            Banner:{' '}
            <a href={link} target="_blank" rel="noreferrer noopener">
              {banner.name || 'unknown'} <IconExternalLink size={14} />
            </a>
          </div>
        );
      }
    }

    const recommenderToken = product.recommenderId;
    for (const page of pages) {
      const recommender = page.recommenders.find((x) => x.token === recommenderToken);
      if (recommender) {
        const link = `#/accounts/${accountId}/domains/${domainId}/pages/${recommender.pageId}/recommenders/${recommender.id}/details`;
        eventElement.push(
          <div key={i++}>
            Recommender:{' '}
            <a href={link} target="_blank" rel="noreferrer noopener">
              {recommender.name || 'unknown'} <IconExternalLink size={14} />
            </a>
          </div>
        );
      }
    }

    // On salesAgentProductClick `id` is the sales agent's, not a product's - the product
    // clicked is on the productView event at the same timestamp.
    if (product.id && event.action !== 'salesAgentProductClick') {
      const link = `#/accounts/${accountId}/domains/${domainId}/products/${product.id}`;
      eventElement.push(
        <div key={i++}>
          Product:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {product.name || product.id} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    // Credit is per line item, so a mixed order shows which item it belongs to.
    const salesAgentId = product.salesAgentId ?? (event.action === 'salesAgentProductClick' ? product.id : null);
    if (salesAgentId) {
      const link = `#/accounts/${accountId}/domains/${domainId}/chatBot`;
      eventElement.push(
        <div key={i++}>
          Sales agent:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {salesAgentId} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    // Not on autocompleteInteraction itself: its label already says it, and every result
    // carries the id.
    if (product.autocompleteId && event.action !== 'autocompleteInteraction') {
      const link = `#/accounts/${accountId}/domains/${domainId}/searchAsYouType`;
      eventElement.push(
        <div key={i++}>
          Search as you type:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {product.autocompleteId} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const campaignId = parseInt(product.campaignId);
    const campaign = campaigns.find((x) => x.id === campaignId);
    if (campaign) {
      const link = `#/accounts/${accountId}/domains/${domainId}/${
        campaign.type === 'workflow' ? 'workflows' : 'campaigns'
      }/${product.campaignId}/details`;
      eventElement.push(
        <div key={i++}>
          Campaign:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {campaign.name || 'unknown'} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const emailId = parseInt(product.emailId);
    const email = emails.find((x) => x.id === emailId);
    if (email) {
      const link = `#/accounts/${accountId}/domains/${domainId}/emails/${product.emailId}/details`;
      eventElement.push(
        <div key={i++}>
          Email:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {email.name || 'unknown'} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const textMessageId = parseInt(product.textMessageId);
    const textMessage = textMessages.find((x) => x.id === textMessageId);
    if (textMessage) {
      const link = `#/accounts/${accountId}/domains/${domainId}/textMessages/${product.textMessageId}/details`;
      eventElement.push(
        <div key={i++}>
          Text message:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {textMessage.name || 'unknown'} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const viberMessageId = parseInt(product.viberMessageId);
    const viberMessage = viberMessages.find((x) => x.id === viberMessageId);
    if (viberMessage) {
      const link = `#/accounts/${accountId}/domains/${domainId}/viber/${product.viberMessageId}/details`;
      eventElement.push(
        <div key={i++}>
          Viber message:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {viberMessage.name || 'unknown'} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const pushNotificationId = parseInt(product.pushNotificationId);
    const pushNotification = pushNotifications.find((x) => x.id === pushNotificationId);
    if (pushNotification) {
      const link = `#/accounts/${accountId}/domains/${domainId}/push/${product.pushNotificationId}/details`;
      eventElement.push(
        <div key={i++}>
          Push notification:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {pushNotification.name || 'unknown'} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const whatsappMessageId = parseInt(product.whatsappMessageId);
    const whatsappMessage = whatsappMessages.find((x) => x.id === whatsappMessageId);
    if (whatsappMessage) {
      const link = `#/accounts/${accountId}/domains/${domainId}/whatsapp/${product.whatsappMessageId}/details`;
      eventElement.push(
        <div key={i++}>
          WhatsApp message:{' '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            {whatsappMessage.name || 'unknown'} <IconExternalLink size={14} />
          </a>
        </div>
      );
    }

    const flowStep = event.data?.flowStep;

    const rules = flowStep?.data?.custom?.values.rules;

    if (rules) {
      eventElement.push(
        <div key={i++}>
          {flowStep.noMatchStep ? 'Did not match' : 'Matched '} condition: (
          {rules
            .map((rule) => {
              return rule.inverseSelection ? `Not ${rule.name}` : rule.name;
            })
            .join(' and ')}
          )
        </div>
      );
    }
  }

  return eventElement;
};

export default ProfileEventLink;
